import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { concat, EMPTY, merge, Observable, of, timer } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  exhaustMap,
  expand,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { LeadPipelineService } from '../../services/lead-pipeline.service';
import { PopupService } from '../../services/popup.service';
import {
  CreatePipelinePayload,
  PipelineExport,
  UpdatePipelinePayload,
  VeridionFilterPayload,
} from '../../models/lead-pipeline.model';
import {
  ActivityActions,
  CompaniesActions,
  DashboardActions,
  DetailActions,
  ExportsActions,
  LeadsActions,
  LifecycleActions,
  ListActions,
  NotificationsActions,
  PersonsActions,
  RunNowActions,
  RunsActions,
  WizardActions,
} from './lead-pipeline.actions';
import {
  selectActivityFilters,
  selectActivityMeta,
  selectCompaniesFilters,
  selectCompaniesMeta,
  selectDashboardChartRange,
  selectDetailCurrent,
  selectLeadsFilters,
  selectLeadsMeta,
  selectListFilters,
  selectListIncludeArchived,
  selectPersonsFilters,
  selectPersonsMeta,
  selectRunsMeta,
  selectWizardFilters,
  selectWizardSessionId,
  selectWizardState,
} from './lead-pipeline.selectors';

/** sessionStorage key for the wizard draft session id (NOT the HTTP header name). */
const WIZARD_SESSION_KEY = 'wizard_session_id';

const EXPORT_TERMINAL = ['ready', 'failed', 'expired'];
const RUN_TERMINAL = ['completed', 'partial', 'failed', 'skipped'];

function payloadsEqual(a: VeridionFilterPayload, b: VeridionFilterPayload): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * RFC4122 v4 UUID with fallbacks: `crypto.randomUUID` is only available in secure
 * contexts (https/localhost), so dev over plain http throws — fall back to
 * `crypto.getRandomValues`, then `Math.random` as a last resort.
 */
function generateUuidV4(): string {
  const g: Crypto | undefined = typeof crypto !== 'undefined' ? crypto : undefined;
  if (g && typeof g.randomUUID === 'function') {
    return g.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => {
    const rand = g && g.getRandomValues ? g.getRandomValues(new Uint8Array(1))[0] % 16 : (Math.random() * 16) | 0;
    const v = ch === 'x' ? rand : (rand & 0x3) | 0x8;
    return v.toString(16);
  });
}

function hasAnyFilter(filters: VeridionFilterPayload): boolean {
  return (filters ?? []).some(c => {
    const v = c?.value;
    if (Array.isArray(v)) return v.length > 0;
    if (v && typeof v === 'object') return Object.values(v).some(x => x !== null && x !== undefined && x !== '');
    return v !== null && v !== undefined && v !== '';
  });
}

@Injectable()
export class LeadPipelineEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private service: LeadPipelineService,
    private popup: PopupService,
    private translate: TranslateService,
    private router: Router,
  ) {}

  private t(key: string, params?: object): string {
    return this.translate.instant(key, params);
  }

  /** Guard so parallel 404/403s (multiple tabs loading at once) don't spam toasts/navigations. */
  private redirecting = false;

  /**
   * Emits when the user leaves the pipeline detail page.
   *
   * Sub-resource loads are cancelled on it: without that, a request still in flight when
   * the user navigates away resolves afterwards and writes pipeline A's rows back into a
   * slice the `cleared` reducer just reset — so pipeline B opens showing A's data until
   * its own response lands. Used inside the switchMap so only the request is cancelled,
   * never the effect stream itself.
   */
  private readonly pipelineLeft$ = this.actions$.pipe(ofType(DetailActions.cleared));

  /**
   * Error handler for the **initial pipeline load** (`loadDetail$`): a 404/403 means
   * the pipeline is gone or access-denied → redirect to the list with a toast (spec §9).
   * Sub-resource loads (companies/persons/leads/dashboard/activity/runs/exports) do NOT
   * redirect — they surface an in-tab `failed` state — so a transient sub-resource error
   * or a background poll never yanks the user off a valid pipeline. De-duped via
   * `redirecting` so a burst can't double-navigate.
   */
  private mapLoadError(error: any, failed: Action): Observable<Action> {
    if ((error?.status === 404 || error?.status === 403) && !this.redirecting) {
      this.redirecting = true;
      this.popup.notify(this.t('lead_pipelines.errors.not_found_or_denied'), '', 'error');
      this.router.navigate(['/leads/pipelines']).finally(() => {
        // Allow a future redirect after this navigation settles.
        setTimeout(() => (this.redirecting = false), 1000);
      });
    }
    return of(failed);
  }

  // ===========================================================================
  // List
  // ===========================================================================
  reloadListOnFilterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ListActions.filtersUpdated, ListActions.includeArchivedToggled),
      map(() => ListActions.loadRequested()),
    ),
  );

  loadList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ListActions.loadRequested),
      withLatestFrom(this.store.select(selectListFilters), this.store.select(selectListIncludeArchived)),
      switchMap(([, filters, includeArchived]) =>
        this.service
          .list({
            status: filters.status,
            sort: filters.sort,
            include_archived: includeArchived || undefined,
          })
          .pipe(
            map(res => ListActions.loaded({ items: res.data, meta: res.meta, tabs: res.tabs })),
            catchError(error => of(ListActions.failed({ error }))),
          ),
      ),
    ),
  );

  // Refresh the list (and its tab counts) after any lifecycle mutation.
  refreshListAfterLifecycle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LifecycleActions.mutated, LifecycleActions.deleted),
      map(() => ListActions.loadRequested()),
    ),
  );

  // ===========================================================================
  // Detail
  // ===========================================================================
  // Initial detail load — a 404/403 here means the pipeline is gone/denied → redirect.
  loadDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DetailActions.requested),
      switchMap(({ uuid }) =>
        this.service.get(uuid).pipe(
          map(pipeline => DetailActions.loaded({ pipeline })),
          catchError(error => this.mapLoadError(error, DetailActions.failed({ error }))),
        ),
      ),
    ),
  );

  // Background refresh (Echo / 30s poll) — keep current data on a transient error;
  // never yank the user off the page mid-work.
  refreshDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DetailActions.refreshed),
      switchMap(({ uuid }) =>
        this.service.get(uuid).pipe(
          map(pipeline => DetailActions.loaded({ pipeline })),
          catchError(() => EMPTY),
        ),
      ),
    ),
  );

  // ===========================================================================
  // Lifecycle
  // ===========================================================================
  pause$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LifecycleActions.pauseRequested),
      exhaustMap(({ uuid }) =>
        this.service.pause(uuid).pipe(
          map(pipeline => {
            this.popup.notify(this.t('lead_pipelines.toasts.paused'), '', 'success');
            return LifecycleActions.mutated({ pipeline });
          }),
          catchError(error => of(LifecycleActions.failed({ error }))),
        ),
      ),
    ),
  );

  resume$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LifecycleActions.resumeRequested),
      exhaustMap(({ uuid }) =>
        this.service.resume(uuid).pipe(
          map(pipeline => {
            this.popup.notify(this.t('lead_pipelines.toasts.resumed'), '', 'success');
            return LifecycleActions.mutated({ pipeline });
          }),
          catchError(error => of(LifecycleActions.failed({ error }))),
        ),
      ),
    ),
  );

  archive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LifecycleActions.archiveRequested),
      exhaustMap(({ uuid }) =>
        this.service.archive(uuid).pipe(
          map(pipeline => {
            this.popup.notify(this.t('lead_pipelines.toasts.archived'), '', 'success');
            return LifecycleActions.mutated({ pipeline });
          }),
          catchError(error => of(LifecycleActions.failed({ error }))),
        ),
      ),
    ),
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LifecycleActions.deleteRequested),
      exhaustMap(({ uuid }) =>
        this.service.delete(uuid).pipe(
          map(() => {
            this.popup.notify(this.t('lead_pipelines.toasts.deleted'), '', 'success');
            return LifecycleActions.deleted({ uuid });
          }),
          catchError(error => of(LifecycleActions.failed({ error }))),
        ),
      ),
    ),
  );

  /** Detail page: archive (ignore if already archived) then hard-delete, then go to the list. */
  archiveThenDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LifecycleActions.archiveAndDeleteRequested),
      exhaustMap(({ uuid }) =>
        this.service.archive(uuid).pipe(
          catchError(() => of(null)),
          switchMap(() =>
            this.service.delete(uuid).pipe(
              map(() => {
                this.popup.notify(this.t('lead_pipelines.toasts.deleted'), '', 'success');
                this.router.navigate(['/leads/pipelines']);
                return LifecycleActions.deleted({ uuid });
              }),
              catchError(error => of(LifecycleActions.failed({ error }))),
            ),
          ),
        ),
      ),
    ),
  );

  lifecycleFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LifecycleActions.failed),
        tap(({ error }) => {
          const msg = error?.error?.message || this.t('lead_pipelines.errors.generic');
          this.popup.notify(msg, '', 'error');
        }),
      ),
    { dispatch: false },
  );

  // ===========================================================================
  // Dashboard
  // ===========================================================================
  loadDashboard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.requested),
      withLatestFrom(this.store.select(selectDashboardChartRange)),
      switchMap(([{ uuid }, range]) =>
        this.service.getDashboard(uuid, `${range}d` as '7d' | '30d' | '90d').pipe(
          map(tiles =>
            DashboardActions.loaded({ tiles, chart: tiles.daily_lead_generation_chart ?? [] }),
          ),
          catchError(error => of(DashboardActions.failed({ error }))),
          takeUntil(this.pipelineLeft$),
        ),
      ),
    ),
  );

  reloadDashboardOnRangeChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.chartRangeChanged),
      withLatestFrom(this.store.select(selectDetailCurrent)),
      filter(([, detail]) => !!detail),
      map(([, detail]) => DashboardActions.requested({ uuid: detail!.uuid })),
    ),
  );

  // ===========================================================================
  // Companies
  // ===========================================================================
  loadCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.requested),
      withLatestFrom(this.store.select(selectCompaniesFilters)),
      switchMap(([{ uuid }, filters]) =>
        this.service.listCompanies(uuid, { ...filters, page: 1 }).pipe(
          map(res => CompaniesActions.loaded({ items: res.data, meta: res.meta, append: false })),
          catchError(error => of(CompaniesActions.failed({ error }))),
          takeUntil(this.pipelineLeft$),
        ),
      ),
    ),
  );

  loadMoreCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.loadMoreRequested),
      withLatestFrom(this.store.select(selectCompaniesFilters), this.store.select(selectCompaniesMeta)),
      switchMap(([{ uuid }, filters, meta]) =>
        this.service.listCompanies(uuid, { ...filters, page: meta.current_page + 1 }).pipe(
          map(res => CompaniesActions.loaded({ items: res.data, meta: res.meta, append: true })),
          catchError(error => of(CompaniesActions.failed({ error }))),
          takeUntil(this.pipelineLeft$),
        ),
      ),
    ),
  );

  loadCompaniesPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.pageChanged),
      withLatestFrom(this.store.select(selectCompaniesFilters)),
      switchMap(([{ uuid, page }, filters]) =>
        this.service.listCompanies(uuid, { ...filters, page }).pipe(
          map(res => CompaniesActions.loaded({ items: res.data, meta: res.meta, append: false })),
          catchError(error => of(CompaniesActions.failed({ error }))),
          takeUntil(this.pipelineLeft$),
        ),
      ),
    ),
  );

  reloadCompaniesOnFilterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.filtersUpdated),
      withLatestFrom(this.store.select(selectDetailCurrent)),
      filter(([, detail]) => !!detail),
      map(([, detail]) => CompaniesActions.requested({ uuid: detail!.uuid })),
    ),
  );

  // ===========================================================================
  // Persons
  // ===========================================================================
  loadPersons$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PersonsActions.requested),
      withLatestFrom(this.store.select(selectPersonsFilters)),
      switchMap(([{ uuid }, filters]) =>
        this.service.listPersons(uuid, { ...filters, page: 1 }).pipe(
          map(res => PersonsActions.loaded({ items: res.data, meta: res.meta, append: false })),
          catchError(error => of(PersonsActions.failed({ error }))),
          takeUntil(this.pipelineLeft$),
        ),
      ),
    ),
  );

  loadMorePersons$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PersonsActions.loadMoreRequested),
      withLatestFrom(this.store.select(selectPersonsFilters), this.store.select(selectPersonsMeta)),
      switchMap(([{ uuid }, filters, meta]) =>
        this.service.listPersons(uuid, { ...filters, page: meta.current_page + 1 }).pipe(
          map(res => PersonsActions.loaded({ items: res.data, meta: res.meta, append: true })),
          catchError(error => of(PersonsActions.failed({ error }))),
          takeUntil(this.pipelineLeft$),
        ),
      ),
    ),
  );

  loadPersonsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PersonsActions.pageChanged),
      withLatestFrom(this.store.select(selectPersonsFilters)),
      switchMap(([{ uuid, page }, filters]) =>
        this.service.listPersons(uuid, { ...filters, page }).pipe(
          map(res => PersonsActions.loaded({ items: res.data, meta: res.meta, append: false })),
          catchError(error => of(PersonsActions.failed({ error }))),
          takeUntil(this.pipelineLeft$),
        ),
      ),
    ),
  );

  reloadPersonsOnFilterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PersonsActions.filtersUpdated),
      withLatestFrom(this.store.select(selectDetailCurrent)),
      filter(([, detail]) => !!detail),
      map(([, detail]) => PersonsActions.requested({ uuid: detail!.uuid })),
    ),
  );

  unlockEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PersonsActions.unlockEmailRequested),
      mergeMap(({ uuid, personUuid }) =>
        this.service.unlockEmail(uuid, personUuid).pipe(
          map(result => {
            this.popup.notify(this.t('lead_pipelines.toasts.email_unlocked'), '', 'success');
            return PersonsActions.emailUnlocked({ result });
          }),
          catchError(error => {
            const msg =
              error?.status === 409
                ? this.t('lead_pipelines.errors.unlock_archived')
                : error?.error?.message || this.t('lead_pipelines.errors.generic');
            this.popup.notify(msg, '', 'error');
            return of(PersonsActions.unlockEmailFailed({ personUuid, error }));
          }),
        ),
      ),
    ),
  );

  // ===========================================================================
  // Qualified leads
  // ===========================================================================
  loadLeads$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LeadsActions.requested),
      withLatestFrom(this.store.select(selectLeadsFilters)),
      switchMap(([{ uuid }, filters]) =>
        this.service.listLeads(uuid, { tab: filters.tab, sort: filters.sort, page: 1 }).pipe(
          map(res => LeadsActions.loaded({ items: res.data, meta: res.meta, append: false })),
          catchError(error => of(LeadsActions.failed({ error }))),
          takeUntil(this.pipelineLeft$),
        ),
      ),
    ),
  );

  loadMoreLeads$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LeadsActions.loadMoreRequested),
      withLatestFrom(this.store.select(selectLeadsFilters), this.store.select(selectLeadsMeta)),
      switchMap(([{ uuid }, filters, meta]) =>
        this.service
          .listLeads(uuid, { tab: filters.tab, sort: filters.sort, page: meta.current_page + 1 })
          .pipe(
            map(res => LeadsActions.loaded({ items: res.data, meta: res.meta, append: true })),
            catchError(error => of(LeadsActions.failed({ error }))),
            takeUntil(this.pipelineLeft$),
          ),
      ),
    ),
  );

  reloadLeadsOnFilterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LeadsActions.tabChanged, LeadsActions.sortChanged),
      withLatestFrom(this.store.select(selectDetailCurrent)),
      filter(([, detail]) => !!detail),
      map(([, detail]) => LeadsActions.requested({ uuid: detail!.uuid })),
    ),
  );

  loadLeadDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LeadsActions.detailRequested),
      switchMap(({ uuid, leadUuid }) =>
        this.service.getLead(uuid, leadUuid).pipe(
          map(lead => LeadsActions.detailLoaded({ lead })),
          catchError(error => of(LeadsActions.detailFailed({ error }))),
        ),
      ),
    ),
  );

  updateLeadStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LeadsActions.statusUpdateRequested),
      mergeMap(({ uuid, leadUuid, status }) =>
        this.service.updateLeadStatus(uuid, leadUuid, { status }).pipe(
          map(lead => LeadsActions.statusUpdated({ lead })),
          catchError(error => {
            this.popup.notify(error?.error?.message || this.t('lead_pipelines.errors.generic'), '', 'error');
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  submitFeedback$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LeadsActions.feedbackRequested),
      mergeMap(({ uuid, leadUuid, feedback, notes }) =>
        this.service.submitFeedback(uuid, leadUuid, { feedback, feedback_notes: notes }).pipe(
          map(lead => LeadsActions.feedbackSubmitted({ lead })),
          catchError(error => {
            this.popup.notify(error?.error?.message || this.t('lead_pipelines.errors.generic'), '', 'error');
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  enrichLead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LeadsActions.enrichRequested),
      mergeMap(({ uuid, leadUuid, workspaceId }) =>
        this.service.enrichForWorkspace(uuid, leadUuid, Number(workspaceId)).pipe(
          map(result => {
            this.popup.notify(this.t('lead_pipelines.toasts.enrich_queued'), '', 'success');
            return LeadsActions.enriched({ result });
          }),
          catchError(error => {
            this.popup.notify(error?.error?.message || this.t('lead_pipelines.errors.generic'), '', 'error');
            return of(LeadsActions.enrichFailed({ error }));
          }),
        ),
      ),
    ),
  );

  copyToCrm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LeadsActions.copyToCrmRequested),
      mergeMap(({ uuid, leadUuid }) =>
        this.service.copyToCrm(uuid, leadUuid).pipe(
          map(() => {
            this.popup.notify(this.t('lead_pipelines.toasts.copied_to_crm'), '', 'success');
            return LeadsActions.copiedToCrm({ leadUuid });
          }),
          catchError(error => {
            this.popup.notify(error?.error?.message || this.t('lead_pipelines.errors.generic'), '', 'error');
            return of(LeadsActions.copyToCrmFailed({ error }));
          }),
        ),
      ),
    ),
  );

  enhanceLead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LeadsActions.enhanceRequested),
      mergeMap(({ uuid, leadUuid }) =>
        this.service.enhanceLead(uuid, leadUuid).pipe(
          map((res: any) => {
            this.popup.notify(this.t('lead_pipelines.toasts.enhance_started'), '', 'success');
            // enhance returns the created search id; store it so the card can flip
            // to "View Profile" and deep-link to /report/:id without a reload.
            const reportId = res?.search_id ?? res?.data?.search_id ?? res?.enhanced_report_id ?? null;
            return LeadsActions.enhanced({ leadUuid, reportId });
          }),
          catchError(error => {
            this.popup.notify(error?.error?.message || this.t('lead_pipelines.errors.generic'), '', 'error');
            return of(LeadsActions.enhanceFailed({ error }));
          }),
        ),
      ),
    ),
  );

  sendEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LeadsActions.sendEmailRequested),
      mergeMap(({ uuid, leadUuid }) =>
        this.service.sendEmail(uuid, leadUuid).pipe(
          map(() => {
            this.popup.notify(this.t('lead_pipelines.toasts.email_coming_soon'), '', 'info');
            return LeadsActions.sendEmailRecorded({ leadUuid });
          }),
          catchError(() => {
            this.popup.notify(this.t('lead_pipelines.toasts.email_coming_soon'), '', 'info');
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  // ===========================================================================
  // Activity
  // ===========================================================================
  loadActivity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActivityActions.requested),
      withLatestFrom(this.store.select(selectActivityFilters)),
      switchMap(([{ uuid }, filters]) =>
        this.service.listActivity(uuid, { ...filters, page: 1 }).pipe(
          map(res => ActivityActions.loaded({ items: res.data, meta: res.meta, append: false })),
          catchError(error => of(ActivityActions.failed({ error }))),
          takeUntil(this.pipelineLeft$),
        ),
      ),
    ),
  );

  loadMoreActivity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActivityActions.loadMoreRequested),
      withLatestFrom(this.store.select(selectActivityFilters), this.store.select(selectActivityMeta)),
      switchMap(([{ uuid }, filters, meta]) =>
        this.service.listActivity(uuid, { ...filters, page: meta.current_page + 1 }).pipe(
          map(res => ActivityActions.loaded({ items: res.data, meta: res.meta, append: true })),
          catchError(error => of(ActivityActions.failed({ error }))),
          takeUntil(this.pipelineLeft$),
        ),
      ),
    ),
  );

  reloadActivityOnFilterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActivityActions.filterChanged),
      withLatestFrom(this.store.select(selectDetailCurrent)),
      filter(([, detail]) => !!detail),
      map(([, detail]) => ActivityActions.requested({ uuid: detail!.uuid })),
    ),
  );

  // ===========================================================================
  // Runs
  // ===========================================================================
  loadRuns$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RunsActions.requested),
      switchMap(({ uuid }) =>
        this.service.listRuns(uuid, { page: 1 }).pipe(
          map(res => RunsActions.loaded({ items: res.data, meta: res.meta, append: false })),
          catchError(error => of(RunsActions.failed({ error }))),
          takeUntil(this.pipelineLeft$),
        ),
      ),
    ),
  );

  loadMoreRuns$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RunsActions.loadMoreRequested),
      withLatestFrom(this.store.select(selectRunsMeta)),
      switchMap(([{ uuid }, meta]) =>
        this.service.listRuns(uuid, { page: meta.current_page + 1 }).pipe(
          map(res => RunsActions.loaded({ items: res.data, meta: res.meta, append: true })),
          catchError(error => of(RunsActions.failed({ error }))),
          takeUntil(this.pipelineLeft$),
        ),
      ),
    ),
  );

  loadRunDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RunsActions.detailRequested),
      switchMap(({ uuid, runUuid }) =>
        this.service.getRun(uuid, runUuid).pipe(
          map(run => RunsActions.detailLoaded({ run })),
          catchError(error => of(RunsActions.detailFailed({ error }))),
        ),
      ),
    ),
  );

  // ===========================================================================
  // Notifications
  // ===========================================================================
  loadNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsActions.requested),
      switchMap(({ uuid }) =>
        this.service.listNotifications(uuid).pipe(
          map(res =>
            NotificationsActions.loaded({ items: res.data, unreadCount: res.meta.unread_count ?? 0 }),
          ),
          catchError(error => of(NotificationsActions.failed({ error }))),
        ),
      ),
    ),
  );

  // ===========================================================================
  // Exports
  // ===========================================================================
  loadExports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExportsActions.requested),
      switchMap(({ uuid }) =>
        this.service.listExports(uuid).pipe(
          map(res => ExportsActions.loaded({ items: res.data })),
          catchError(error => of(ExportsActions.failed({ error }))),
          takeUntil(this.pipelineLeft$),
        ),
      ),
    ),
  );

  createExport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExportsActions.createRequested),
      exhaustMap(({ uuid, entity, filtersApplied }) =>
        this.service.exportCsv(uuid, entity, filtersApplied).pipe(
          map(started => {
            this.popup.notify(this.t('lead_pipelines.toasts.export_queued'), '', 'success');
            return ExportsActions.createStarted({ export: started });
          }),
          catchError(error => {
            this.popup.notify(error?.error?.message || this.t('lead_pipelines.errors.generic'), '', 'error');
            return of(ExportsActions.createFailed({ entity, error }));
          }),
        ),
      ),
    ),
  );

  /** Poll a freshly-started export every 3s with exponential back-off up to 60s. */
  pollExport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExportsActions.createStarted),
      mergeMap(({ export: started }) => this.pollExport(started.export_uuid)),
    ),
  );

  /** When the exports list loads, poll any rows still in flight (pending/processing). */
  pollInFlightExports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExportsActions.loaded),
      mergeMap(({ items }) =>
        merge(
          ...items
            .filter(e => e.status === 'pending' || e.status === 'processing')
            .map(e => this.pollExport(e.uuid)),
        ),
      ),
    ),
  );

  /** Fetch the signed download URL then open it (the export-show response carries `download_url`). */
  downloadExport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ExportsActions.downloadRequested),
        mergeMap(({ exportUuid }) =>
          this.service.getExport(exportUuid).pipe(
            tap(exp => {
              if (exp.download_url) {
                window.open(exp.download_url, '_blank');
              } else {
                window.open(this.service.getExportDownloadUrl(exportUuid), '_blank');
              }
            }),
            map(exp => ExportsActions.exportUpdated({ export: exp })),
            catchError(() => EMPTY),
          ),
        ),
      ),
  );

  /**
   * Stops when the user leaves the pipeline: `exportUpdated` prepends an export it does
   * not recognise, so a poll that outlived its pipeline would drop a stale row into the
   * next pipeline's Exports tab. Re-entering the tab reloads and resumes polling whatever
   * is still in flight.
   */
  private pollExport(exportUuid: string): Observable<Action> {
    const MAX_INTERVAL = 60000;
    let attempt = 0;
    return timer(3000).pipe(
      switchMap(() => this.service.getExport(exportUuid)),
      expand((exp: PipelineExport) => {
        if (EXPORT_TERMINAL.includes(exp.status)) {
          return EMPTY;
        }
        attempt += 1;
        const interval = Math.min(3000 * 2 ** attempt, MAX_INTERVAL);
        return timer(interval).pipe(switchMap(() => this.service.getExport(exportUuid)));
      }),
      map(exp => ExportsActions.exportUpdated({ export: exp })),
      catchError(error => of(ExportsActions.exportFailed({ exportUuid, error }))),
      takeUntil(this.pipelineLeft$),
    );
  }

  // ===========================================================================
  // Run-now
  // ===========================================================================
  runNow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RunNowActions.requested),
      exhaustMap(({ uuid, auto }) =>
        this.service.runNow(uuid).pipe(
          map(res => {
            if (res.in_flight) {
              this.popup.notify(this.t('lead_pipelines.toasts.run_in_progress'), '', 'info');
              return RunNowActions.inFlight({ uuid, runUuid: res.run_uuid });
            }
            this.popup.notify(
              this.t(auto ? 'lead_pipelines.toasts.first_run_started' : 'lead_pipelines.toasts.run_dispatched'),
              '',
              'success',
            );
            return RunNowActions.dispatched({
              uuid,
              runUuid: res.run_uuid,
              creditsDeducted: res.credits_deducted,
            });
          }),
          catchError(error => {
            this.popup.notify(error?.error?.message || this.t('lead_pipelines.errors.generic'), '', 'error');
            return of(RunNowActions.failed({ uuid, error }));
          }),
        ),
      ),
    ),
  );

  /**
   * After a manual run is dispatched (or found already in flight), poll the run
   * every 10s until terminal, then flip `runNow.completed`. This works without
   * Reverb broadcasts (spec Open Q #2 fallback); the detail Echo subscription
   * will short-circuit it if the backend does broadcast `run.completed`.
   */
  runNowPoll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RunNowActions.dispatched, RunNowActions.inFlight),
      mergeMap(({ uuid, runUuid }) =>
        timer(10000, 10000).pipe(
          take(180), // hard cap (~30 min) so a stuck run never polls forever
          switchMap(() => this.service.getRun(uuid, runUuid).pipe(catchError(() => of(null)))),
          filter(run => !!run),
          takeWhile((run: any) => !RUN_TERMINAL.includes(run.status), true),
          filter((run: any) => RUN_TERMINAL.includes(run.status)),
          take(1),
          map(() => RunNowActions.completed({ uuid })),
          // Stop polling if completion arrives via Echo, or the user leaves the pipeline.
          takeUntil(
            this.actions$.pipe(
              ofType(RunNowActions.completed, DetailActions.cleared),
              filter((a: any) => a.type === DetailActions.cleared.type || a.uuid === uuid),
            ),
          ),
        ),
      ),
    ),
  );

  /** On run completion, refresh the surfaces a run affects. */
  runNowCompletedRefresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RunNowActions.completed),
      mergeMap(({ uuid }) => [
        DetailActions.refreshed({ uuid }),
        DashboardActions.requested({ uuid }),
        LeadsActions.requested({ uuid }),
      ]),
    ),
  );

  // ===========================================================================
  // Wizard — session, preview-counts, edit prefill, launch, update
  // ===========================================================================
  wizardSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WizardActions.entered),
      map(() => {
        let sessionId = sessionStorage.getItem(WIZARD_SESSION_KEY);
        if (!sessionId) {
          sessionId = generateUuidV4();
          sessionStorage.setItem(WIZARD_SESSION_KEY, sessionId);
        }
        return WizardActions.sessionStarted({ sessionId });
      }),
    ),
  );

  wizardEditPrefill$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WizardActions.entered),
      filter(({ mode, uuid }) => mode === 'edit' && !!uuid),
      switchMap(({ uuid }) =>
        this.service.get(uuid as string).pipe(
          map(pipeline => WizardActions.editLoaded({ pipeline })),
          // Can't edit a pipeline we couldn't load — bail to the list rather than
          // leaving the wizard in a broken/empty state with a misleading preview error.
          catchError(error => {
            const key =
              error?.status === 404 || error?.status === 403
                ? 'lead_pipelines.errors.not_found_or_denied'
                : 'lead_pipelines.errors.generic';
            this.popup.notify(this.t(key), '', 'error');
            this.router.navigate(['/leads/pipelines']);
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  /** After edit prefill, kick off a preview so the banner + runtime estimate populate. */
  editLoadedPreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WizardActions.editLoaded),
      filter(({ pipeline }) => hasAnyFilter((pipeline.filters as any) ?? [])),
      map(({ pipeline }) => WizardActions.filtersUpdated({ filters: (pipeline.filters as any) ?? [] })),
    ),
  );

  previewCounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WizardActions.filtersUpdated),
      map(({ filters }) => filters),
      filter(filters => hasAnyFilter(filters)),
      // Debounce first (settle the rapid edits), THEN distinct so we only fire when
      // the settled payload differs from the last one actually sent.
      debounceTime(300),
      distinctUntilChanged(payloadsEqual),
      withLatestFrom(this.store.select(selectWizardSessionId)),
      switchMap(([filters, sessionId]) =>
        concat(
          of(WizardActions.previewCountsRequested({ filters })),
          this.service.previewCounts(filters, sessionId).pipe(
            map(counts => WizardActions.previewCountsLoaded({ counts })),
            catchError(error => {
              if (error?.status === 429) {
                const retryAfter = Number(
                  error?.error?.retry_after ?? error?.headers?.get?.('Retry-After') ?? 30,
                );
                return of(WizardActions.previewRateLimited({ retryAfter }));
              }
              return of(WizardActions.previewFailed({ error }));
            }),
          ),
        ),
      ),
    ),
  );

  previewRetry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WizardActions.previewRetryRequested),
      withLatestFrom(
        this.store.select(selectWizardFilters),
        this.store.select(selectWizardSessionId),
      ),
      switchMap(([, filters, sessionId]) =>
        this.service.previewCounts(filters, sessionId).pipe(
          map(counts => WizardActions.previewCountsLoaded({ counts })),
          catchError(error => {
            if (error?.status === 429) {
              const retryAfter = Number(
                error?.error?.retry_after ?? error?.headers?.get?.('Retry-After') ?? 30,
              );
              return of(WizardActions.previewRateLimited({ retryAfter }));
            }
            return of(WizardActions.previewFailed({ error }));
          }),
        ),
      ),
    ),
  );

  launch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WizardActions.launchRequested),
      withLatestFrom(this.store.select(selectWizardState)),
      exhaustMap(([, wizard]) => {
        const payload: CreatePipelinePayload = {
          title: wizard.title,
          description: wizard.description || null,
          filters: wizard.filters,
          leads_per_day: (wizard.leadsPerDay ?? 10) as 5 | 10,
          workspace_id: Number(wizard.workspaceId),
        };
        return this.service.create(payload).pipe(
          map(pipeline => WizardActions.launchSucceeded({ pipeline })),
          catchError(error => {
            this.handleWizardSubmitError(error);
            return of(WizardActions.launchFailed({ error }));
          }),
        );
      }),
    ),
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WizardActions.updateRequested),
      withLatestFrom(this.store.select(selectWizardState)),
      exhaustMap(([, wizard]) => {
        // Steps 1–2 are immutable while the pipeline is not paused (spec §4.2);
        // only PATCH title/description in that case, never filters/workspace/pace.
        const locked = wizard.mode === 'edit' && wizard.editStatus !== null && wizard.editStatus !== 'paused';
        const payload: UpdatePipelinePayload = locked
          ? { title: wizard.title, description: wizard.description || null }
          : {
              title: wizard.title,
              description: wizard.description || null,
              filters: wizard.filters,
              leads_per_day: (wizard.leadsPerDay ?? undefined) as 5 | 10 | undefined,
              workspace_id: wizard.workspaceId ? Number(wizard.workspaceId) : undefined,
            };
        return this.service.update(wizard.editUuid as string, payload).pipe(
          map(pipeline => WizardActions.updateSucceeded({ pipeline })),
          catchError(error => {
            this.handleWizardSubmitError(error);
            return of(WizardActions.updateFailed({ error }));
          }),
        );
      }),
    ),
  );

  private handleWizardSubmitError(error: any): void {
    if (error?.status === 409) {
      const code = error?.error?.code || error?.error?.error;
      if (code === 'workspace_has_active_pipelines') {
        this.popup.notify(this.t('lead_pipelines.errors.workspace_has_active_pipelines'), '', 'error');
        return;
      }
      this.popup.notify(this.t('lead_pipelines.errors.filter_patch_while_running'), '', 'error');
      return;
    }
    if (error?.status !== 422) {
      // 422 is rendered inline by the form; everything else gets a toast.
      this.popup.notify(error?.error?.message || this.t('lead_pipelines.errors.generic'), '', 'error');
    }
  }

  wizardSubmitSucceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WizardActions.launchSucceeded, WizardActions.updateSucceeded),
        tap(({ pipeline }) => {
          sessionStorage.removeItem(WIZARD_SESSION_KEY);
          this.router.navigate(['/leads/pipelines', pipeline.uuid]);
        }),
        map(() => WizardActions.cleared()),
      ),
  );

  /**
   * Kick off the first run automatically right after a pipeline is created, so
   * Qualified Leads populate without the user having to press "Get Leads".
   * Create-only (not edit/update): the backend `create()` merely schedules
   * `next_run_at`, it does not run immediately. Reuses the full run-now chain
   * (`runNow$` → `runNowPoll$` → `runNowCompletedRefresh$`) and its credit/error
   * handling. Bypasses the confirm modal by design — creating the pipeline is
   * the explicit opt-in to generate leads.
   */
  autoRunAfterLaunch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WizardActions.launchSucceeded),
      map(({ pipeline }) => RunNowActions.requested({ uuid: pipeline.uuid, auto: true })),
    ),
  );

  wizardClearedSession$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WizardActions.cleared),
        tap(() => sessionStorage.removeItem(WIZARD_SESSION_KEY)),
      ),
    { dispatch: false },
  );
}
